import { formatMoney } from './DailyReportTable'
import { ATTRIBUTION_CHANNEL_LABELS } from './AttributionCreditTable'
import AttributionModelDetails from './AttributionModelDetails'
import { missingDaysBrief, missingDaysNote } from '../lib/spendCoverage'

const count = value => value.toLocaleString('en-US', { maximumFractionDigits: 3 })
const money = value => value == null ? '—' : formatMoney(value)

export default function AttributionChannelTable({ comparison, scope, lookbackDays }) {
  const model = comparison.models[0]
  if (!model) return null
  return <section className="space-y-3">
    <AttributionModelDetails model={model} scope={scope} lookbackDays={lookbackDays} attributionWindow={comparison.attributionWindow} accountingMode={comparison.accountingMode} />
    <p className="text-sm text-muted-foreground">Model CAC is ad spend divided by attributed new-sale credit, not unique customers. Sales can be fractional. Spend sums every day of the selected dates that ad spend has landed for; a channel short of a day is marked beneath its figure rather than withheld. CAC is $0 when there is new-sale credit and no spend; ROAS is unavailable when spend is zero. Team commissions and other acquisition costs are not included.</p>
    {model.reconciles === false && <p className="text-sm text-muted-foreground">This model gives full credit to each qualifying channel. Sales and revenue overlap across channels; its CAC and ROAS reflect that overlapping credit.</p>}
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm tabular-nums">
        <caption className="sr-only">Channel performance using {model.label}</caption>
        <thead className="bg-muted/50"><tr>{['Channel', 'Attributed sales', 'New-sale credit', 'Attributed revenue', 'Attributed AOV', 'Ad spend', 'Model CAC', 'Attributed ROAS'].map((label, index) => <th key={label} scope="col" className={`p-3 whitespace-nowrap ${index ? 'text-right' : 'text-left'}`}>{label}</th>)}</tr></thead>
        <tbody>{model.rows.map(row => <tr key={row.channel} className="border-t hover:bg-muted/30">
          <th scope="row" className="p-3 text-left font-medium whitespace-nowrap">{ATTRIBUTION_CHANNEL_LABELS[row.channel] || row.channel}</th>
          <td className="p-3 text-right">{count(row.orders)}</td>
          <td className="p-3 text-right">{count(row.new_orders ?? 0)}</td>
          <td className="p-3 text-right">{money(row.revenue)}</td>
          <td className="p-3 text-right">{money(row.aov)}</td>
          <td className="p-3 text-right">
            {money(row.spend)}
            {row.missing_spend_days?.length ? (
              <span className="block text-xs font-normal text-amber-600 dark:text-amber-400"
                    title={missingDaysNote(row.missing_spend_days, { limit: 366 })}>
                {missingDaysBrief(row.missing_spend_days)}
              </span>
            ) : null}
          </td>
          <td className="p-3 text-right">{money(row.cac)}</td>
          <td className="p-3 text-right">{row.roas == null ? '—' : `${row.roas.toFixed(2)}×`}</td>
        </tr>)}</tbody>
      </table>
    </div>
    <p className="text-xs text-muted-foreground">Outside-window credit is excluded from performance ratios. Google spend is matched to the Google row; a separate branded-search row has no allocated spend. A channel with no matching spend at all uses $0; one missing only some days shows the sum of the days that landed, with those dates named.</p>
  </section>
}
